import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { InviteActorsNodeData } from "@gestionalo/enterprise-graph";
import { apiClient } from "@/services/apiClient";

interface Props {
  nodeData: InviteActorsNodeData;
  enterpriseRequestId: string;
  nodeId: string;
  allPayloadValues: Record<string, unknown>;
  onComplete: (data: Record<string, unknown>) => Promise<void>;
  onBack?: () => void;
}

/**
 * Scan all payload values for __actorCount set by the precondition step.
 * Returns 1 if not found (single tenant = initiator only).
 */
function getActorCountFromPayload(payload: Record<string, unknown>): number {
  for (const value of Object.values(payload)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      if (obj.__actorCount != null) return Number(obj.__actorCount);
    }
  }
  return 1;
}

export function InviteActorsNodeView({
  nodeData,
  enterpriseRequestId,
  nodeId,
  allPayloadValues,
  onComplete,
  onBack,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const label = nodeData.actorLabel?.trim() || "participante";

  // Determine required invite count from the actorCount selected in step 1
  const selectedActorCount = getActorCountFromPayload(allPayloadValues);
  // The initiator is actor 0, so additional tenants = actorCount - 1
  const requiredInvites = selectedActorCount > 1 ? selectedActorCount - 1 : 0;
  // Max from graph config, minus initiator
  const maxFromGraph = (nodeData.maxActors ?? 2) - 1;
  const isInviteRequired = requiredInvites > 0;

  // When required: fixed number of fields. When optional: start with 1, allow up to maxFromGraph.
  const [emails, setEmails] = useState<string[]>(
    () => Array.from({ length: isInviteRequired ? requiredInvites : 1 }, () => ""),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addEmail(): void {
    if (emails.length < maxFromGraph) {
      setEmails((prev) => [...prev, ""]);
    }
  }

  function removeEmail(index: number): void {
    if (emails.length <= 1) return;
    setEmails((prev) => prev.filter((_, i) => i !== index));
  }

  function updateEmail(index: number, value: string): void {
    setEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
  }

  async function handleSubmit(skip = false): Promise<void> {
    const validEmails = skip ? [] : emails.map((e) => e.trim()).filter((e) => e.length > 0);

    if (!skip && validEmails.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalid = validEmails.find((e) => !emailRegex.test(e));
      if (invalid) {
        setError(`Email inválido: ${invalid}`);
        return;
      }
    }

    // If invitations are required, enforce minimum
    if (!skip && isInviteRequired && validEmails.length < requiredInvites) {
      setError(
        t("requests.user.wizard.inviteMinRequired", {
          count: requiredInvites,
          label: label.toLowerCase(),
        }),
      );
      return;
    }

    if (!skip && validEmails.length === 0) {
      return handleSubmit(true);
    }

    setSubmitting(true);
    setError(null);
    try {
      if (validEmails.length > 0) {
        await apiClient.post(`/me/enterprise-requests/${enterpriseRequestId}/invite-participants`, {
          emails: validEmails,
          nodeId,
        });
      }
      await onComplete({ invitedEmails: validEmails });
    } catch {
      setError(t("requests.user.wizard.inviteSubmitError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-3">
            {emails.map((email, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1">
                  <Label className="sr-only">Email {label.toLowerCase()} {idx + 1}</Label>
                  <Input
                    type="email"
                    placeholder={emails.length > 1 ? `${label} ${idx + 1} — correo electronico` : `Correo electronico del ${label.toLowerCase()}`}
                    value={email}
                    onChange={(e) => updateEmail(idx, e.target.value)}
                    disabled={submitting}
                  />
                </div>
                {!isInviteRequired && emails.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => removeEmail(idx)}
                    disabled={submitting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {!isInviteRequired && emails.length < maxFromGraph && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEmail}
              disabled={submitting}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t("requests.user.wizard.addActor", { label: label.toLowerCase() })}
            </Button>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} disabled={submitting}>
              {t("requests.user.wizard.enterpriseFormBack")}
            </Button>
          )}
        </div>
        <Button onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("requests.user.wizard.sendingInvitations")}
            </>
          ) : (
            t("requests.user.wizard.sendInvitationsAndContinue")
          )}
        </Button>
      </div>
    </div>
  );
}
